<template>
  <div
    ref="taskListEl"
    v-hotkey="shortcuts"
    class="unplanned-task-list"
    :class="{ empty: !listTaskIds?.length }"
  >
    <AddTaskButton
      ref="addTaskButtonEl"
      class="add-task-button"
      :add-task-action="{ type: 'task' }"
      :post-validate-action="selectTaskById"
      :metrics-source="'tasks/unplanned'"
      @focus="unselectTask"
    />
    <BaseEmptyTasklist
      v-if="!listTaskIds?.length"
      class="empty-unplanned"
      text="Plan all the tasks that require your attention"
    >
    </BaseEmptyTasklist>
    <Suspense v-else>
      <TaskList
        v-if="listTaskIds"
        :actions="{
          assignee: true,
          open: true,
          postpone: true,
          hierarchy: true,
          schedule: {
            able: true,
            type: 'long',
          },
          notes: true,
          space: false,
        }"
        :pagination="showAll ? undefined : 25"
        :list-key="unplannedListKey"
        :task-ids="listTaskIds"
        :selected-task-id="selectedTaskId"
        :is-editing="isEditing"
        @quit-editing="quitEditing"
        @set-selected-task-el="(el) => setSelectedTaskEl(el)"
        @task-click="(id) => selectTaskById(id)"
      />
      <template #fallback>
        <Loader class="unplanned-loading">
          <BaseSkeletonList />
        </Loader>
      </template>
    </Suspense>
    <div class="unselect-area" @click="unselectTask" />
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, PropType, watch } from "vue";

import AddTaskButton from "@/components/AddTaskButton.vue";
import BaseEmptyTasklist from "@/components/base/BaseEmptyTasklist.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import { useTaskList } from "@/components/task/useTaskList";
import {
  applyTaskListOrder,
  getTaskListOrderStore,
  personalSectionKey,
  taskListKeys,
} from "@/components/tasks/ordering";
import TaskList from "@/components/tasks/TaskList.vue";
import type { TaskScope } from "@/components/tasks/taskScope";
import { useReleaser } from "@/ref";

export default defineComponent({
  name: "Unplanned",
  components: {
    Loader,
    AddTaskButton,
    BaseEmptyTasklist,
    BaseSkeletonList,
    TaskList,
  },
  props: {
    length: { type: Number, required: true },
    // Bypass the list's pagination and render every task, e.g. while the
    // host's find bar is open so the search covers the whole list.
    showAll: { type: Boolean, default: false },
    // When set, restrict the list to the scope's tasks.
    taskScope: {
      type: Object as PropType<TaskScope>,
      required: false,
    },
  },
  emits: {
    "update:length": (_nbr: number) => true,
  },
  async setup(props, { emit }) {
    const profile = inject.profile();
    const controller = inject.controller();
    const console = inject.consoleModal();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const contextMenu = inject.contextMenu();
    const releaser = useReleaser();
    const taskIds = await releaser.add(controller.task.unplanned());
    // Same ranking as Agenda / Planner unplanned (personal org). Org Team
    // Tasks use orgSectionKey with their own org/workspace.
    const unplannedListKey = personalSectionKey(
      profile.value,
      taskListKeys.unplanned
    );
    const listTaskIds = props.taskScope
      ? releaser.add(props.taskScope.scopedTaskIds(taskIds))
      : computed(() => taskIds.value ?? []);
    const orderStore = getTaskListOrderStore(profile.value.user.accountId);
    // Read-only ordered view for keyboard selection; TaskList owns commit.
    const selectableTaskIds = computed(() =>
      applyTaskListOrder(
        listTaskIds.value ?? [],
        orderStore.value.lists[unplannedListKey] ?? []
      )
    );
    releaser.defer(
      watch(
        () => listTaskIds.value?.length,
        (length) => {
          if (length !== undefined) emit("update:length", length);
        },
        { immediate: true }
      )
    );
    const {
      addTaskButtonEl,
      isEditing,
      quitEditing,
      selectedTaskId,
      selectTaskById,
      setSelectedTaskEl,
      shortcuts,
      taskListEl,
      unselectTask,
      release: releaseTaskList,
    } = useTaskList(selectableTaskIds, {
      isInputFocused,
      isModalShown,
      controller,
      console,
      contextMenu,
    });
    releaser.defer(releaseTaskList);
    return {
      addTaskButtonEl,
      listTaskIds,
      unplannedListKey,
      isEditing,
      quitEditing,
      selectedTaskId,
      selectTaskById,
      setSelectedTaskEl,
      shortcuts,
      taskListEl,
      unselectTask,
    };
  },
});
</script>
<style scoped lang="scss">
.unplanned-task-list {
  height: 100%;

  .empty-unplanned {
    button.base-button {
      margin: 0;
    }
  }
  .unselect-area {
    display: flex;
    flex: 1;
  }

  // Match TaskInline reorderable checkbox (8+16+8 = 32px): Add task
  // pad is 16px, so only 16px more margin is needed.
  .add-task-button {
    margin-inline-start: 16px;
    width: calc(100% - 16px);
  }

  :deep(.unplanned-loading) {
    box-sizing: border-box;
    padding-inline: 16px;
    padding-inline-start: 32px;

    .base-skeleton-line {
      column-gap: 12px;

      .icon {
        min-width: 18px;
        max-width: 18px;
        height: 18px;
      }

      .body {
        height: 18px;
      }
    }
  }
}
</style>
